import { databaseManager } from './database/DatabaseManager';
import { offlineSyncManager } from './sync/OfflineSyncManager';
import { logger } from '../utils/logger';

/**
 * Initialization Service
 *
 * Handles app startup initialization including database setup
 * and offline sync manager startup.
 */
export class InitializationService {
  private static instance: InitializationService;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): InitializationService {
    if (!InitializationService.instance) {
      InitializationService.instance = new InitializationService();
    }
    return InitializationService.instance;
  }

  /**
   * Initialize all offline services
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      logger.debug('Initializing offline services...');

      // Initialize database
      await databaseManager.initialize();
      logger.debug('Database initialized');

      // Start sync manager
      await offlineSyncManager.start();
      logger.debug('Sync manager started');

      this.isInitialized = true;
      logger.debug('Offline services initialization complete');
    } catch (error) {
      logger.error('Failed to initialize offline services:', error);
      throw error;
    }
  }

  /**
   * Shutdown all services
   */
  public async shutdown(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      logger.debug('Shutting down offline services...');

      // Stop sync manager
      offlineSyncManager.stop();
      logger.debug('Sync manager stopped');

      // Close database
      await databaseManager.close();
      logger.debug('Database closed');

      this.isInitialized = false;
      logger.debug('Offline services shutdown complete');
    } catch (error) {
      logger.error('Failed to shutdown offline services:', error);
      throw error;
    }
  }

  /**
   * Get initialization status
   */
  public isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get database statistics
   */
  public async getStats(): Promise<{
    journeys: number;
    weeks: number;
    activities: number;
    progress: number;
    pendingSync: number;
  }> {
    if (!this.isInitialized) {
      return {
        journeys: 0,
        weeks: 0,
        activities: 0,
        progress: 0,
        pendingSync: 0,
      };
    }

    return await databaseManager.getStats();
  }
}

// Export singleton instance
export const initializationService = InitializationService.getInstance();
